import {
  IconLayoutDashboard, IconWaveSine, IconRoute, IconCalendar, IconHistory,
  IconInbox, IconHeartbeat, IconMicroscope,
  IconClipboardCheck, IconBook, IconArchive, IconTimeline, IconLifebuoy,
  IconListDetails,
  IconStack2, IconGauge, IconCpu, IconPlugConnected, IconSettings, IconCalendarStats,
} from '@tabler/icons-react'
import type { Icon } from '@tabler/icons-react'

/**
 * The Observatory information architecture.
 *
 * Data, not JSX, so the rail, the breadcrumb trail and (later) a command palette
 * all read from one source. Adding a surface means adding a row here.
 *
 * NAMING: `label` stays recognisable; `subtitle` carries the thematic term. That
 * split is deliberate — an administrator should never have to remember that
 * "The Celestial Chamber" means server health. `description` is the fuller line
 * for page headers.
 *
 * Two reserved words, per the naming decisions:
 *  - "Atlas" is the Cartographer metadata layer, so Report Registry is the
 *    Chart Room instead.
 *  - "Chronicle" means the audit trail, so it belongs to Audit Log and the
 *    incident surface got its own word.
 *
 * ---------------------------------------------------------------------------
 * PRUNED 2026-08-20 (decision A6), from 24 entries to 14.
 *
 * The rail advertised sixteen surfaces that did not exist. Two thirds of a
 * navigation tree resolving to a "coming soon" toast is not a roadmap, it is a
 * product that looks broken. One criterion was applied: an unbuilt entry stays
 * only if a real endpoint serves it today, or the work is actually sequenced.
 *
 * Kept, with the reason: Execution History (`api/etl-jobs` is paged and real;
 * rendered since 2026-09-29 over Etl.EtlRun instead, through
 * api/observatory/executions), Queues (`api/system/dead-letters`, rendered since
 * the same date), Signal Inbox (the one signals surface with a named workstream;
 * built since — see SIGNALS AND HEALTH BUILT below), and Governance Queue +
 * Report Registry — served since build-plan #6 by Observatory's own
 * LighthouseAdmin read controllers (D1a), rendered since 2026-09-29. See
 * OBSERVATORY COMPLETION below. Health Monitoring was kept on the claim that
 * `api/health` served it; later its SCOPE, not its endpoints, was what stood in
 * the way, since source reachability went to Sources and the worker fleet to
 * Workers. The user defined it on 2026-09-29 as one board over those readings,
 * per host — see SIGNALS AND HEALTH BUILT below.
 *
 * Dropped, and why they are not merely deferred:
 *  - Incidents — decided since (doc 05, I1): an escalation layer over signals,
 *    not a saved view. Not built: it needs the signal store first.
 *  - Governance (Signals) — overlapped Governance Queue with no endpoint of its
 *    own.
 *  - Publishing, Access Requests — genuinely absent from Cartographer's shipped
 *    governance model, not merely unsurfaced. **Access Requests came back as a
 *    different surface — see REQUEST BACKSTOP ADDED below.**
 *  - Schedules — Town Crier shipped, in Cartographer, which owns schedule
 *    editing. Duplicating it here would breach the scope fence (Observatory shows
 *    the reason; Cartographer provides the editing experience). **The reasoning
 *    was right and the conclusion wrong — restored as a different surface; see
 *    SCHEDULES RESTORED below.**
 *  - Retirement Candidates — dropped as "entirely derived from usage
 *    telemetry"; wrong on two counts (doc 12 §1) and RESTORED in the
 *    Cartographer section, the Reports section it was defined for being gone.
 *  - Services, Connections — need app-level and external probes that do not
 *    exist; `api/health` deliberately filters external checks out. **Both halves
 *    of this were wrong, in different ways — see SOURCES ADDED below.** The
 *    external probes now exist and this is what Sources renders; the Connections
 *    half was wrong from the start, because its ledger was already being written
 *    to Etl.EtlRunChunk and needed no probe at all. What survives of the reason is
 *    the app-level half: beyond the API's own /api/health, nothing checks
 *    Lighthouse's own services — and Health Monitoring, built since, says so
 *    row by row instead of borrowing a colour.
 *  - Configuration — nobody has decided what it would expose. **Decided since,
 *    and restored — see CONFIGURATION RESTORED below.**
 *
 * Restoring any of these is one row. Advertising them was the expensive part.
 *
 * ---------------------------------------------------------------------------
 * WORKERS RESTORED 2026-09-23. The A6 prune dropped it on the premise that "there
 * is no worker identity, heartbeat or sampling anywhere in the backend", and that
 * clause has been deleted rather than edited: it was false in the same working
 * tree that contained Etl.WorkerHeartbeat, WorkerHeartbeatPublisher,
 * WorkerHeartbeatWorker and ObservatoryWorkersController. A doc-in-code asserting
 * something its own tree disproves is worse than no comment.
 *
 * Restoring it really was one row -- no badgeKey, no useNavBadges work, and the
 * endpoint it renders had been serving the Overview panel the whole time.
 *
 * ---------------------------------------------------------------------------
 * SOURCES ADDED 2026-09-23, and it is the nav entry the walkthrough left open.
 * The A6 prune dropped "Services, Connections" together as needing probes that do
 * not exist. Half of that is now false: ISourceConnectionProbe lives in
 * Caravan.Abstractions, Oracle implements it, SourceHealthWorker publishes every
 * 60 seconds into Etl.SourceConnectionHealth, and ObservatorySourcesController
 * serves it. The clause above has been narrowed rather than left standing.
 *
 * NAMING — read this before adding the next row, because the obvious name is
 * taken. This page came out of the **Services (Stations)** review, entry 7, which
 * produced source health instead of the page it set out to assess and left its own
 * nav entry undefined. It is NOT **Connections (Moorings)**, which is entry 9,
 * doc 15, and a different surface: the per-connection ledger over Etl.EtlRunChunk
 * — every connection opened and closed across thirty days, with errors and
 * traffic. Current state versus history. Labelling this one "Connections" would
 * have left that page nameless and sent anyone looking for thirty days of
 * connection history to a projection that keeps none.
 *
 * So: label "Sources", because the row answers "can we reach the systems we pull
 * from"; codename *Stations*, which entry 7 already owned and which fits a place a
 * caravan stops to resupply better than it ever fitted an app-service inventory.
 * The app-service half of entry 7 went to Health Monitoring (Beacons) below,
 * defined and built on 2026-09-29 — see SIGNALS AND HEALTH BUILT.
 *
 * It sits in SYSTEM beside Workers rather than in Signals, both because doc 00
 * files entries 7 and 9 there and because when the ledger lands it belongs next to
 * this row: an operator who finds a source unreachable here reads its history
 * there, and the two being one click apart is the point.
 *
 * ---------------------------------------------------------------------------
 * CONNECTIONS RESTORED 2026-09-24 — entry 9, and the row Sources deliberately did
 * not take. The other half of the A6 prune's "external probes that do not exist",
 * and that premise was wrong from the start here rather than merely outdated:
 * Etl.EtlRunChunk was ALREADY a 1:1 record of one Oracle connection and nobody
 * knew, so this needed no probe at all. It was a retention change, three bug
 * fixes and a read.
 *
 * It is the append-only counterpart to Sources directly above, NOT a richer
 * version of it, and the rail deliberately shows them as a pair: Sources is one
 * row per source overwritten every sixty seconds ("can we reach it now"), this is
 * every connection opened over thirty days and overwrites nothing ("what
 * happened"). Neither answers the other's question. The page is a paged table
 * rather than a card per row because thirty days is up to ~6,300 connections —
 * the form the two rows above use does not survive that scale, and doc 15 says so.
 *
 * ---------------------------------------------------------------------------
 * CONFIGURATION RESTORED 2026-09-24 — entry 10, *Helm*, and the one dropped entry
 * whose reason was not a technical claim: "nobody has decided what it would
 * expose" was true, and doc 16 decided it. Three tiers, deny-by-default with an
 * explicit allowlist, read-only first.
 *
 * It is PER HOST, which doc 16 itself missed: DataQuality and Notifications are
 * bound in Automaton alone, so the API's own configuration could only ever say
 * "no opinion" about the two settings the page exists to surface. Automaton
 * projects its own allowlisted settings into Etl.HostConfiguration (D14 once
 * more) and the page shows each host in its own column.
 *
 * ---------------------------------------------------------------------------
 * SCHEDULES RESTORED 2026-09-24 — entry 5, *Tide Tables*, doc 11. Not a port of
 * Town Crier, which is why the A6 reason above does not apply to it: Cartographer
 * owns the schedule, Observatory owns the CONSEQUENCE. "I have 200 reports
 * scheduled at 3pm — what are they, and will this break stuff" is a question
 * about the worker pool, which Cartographer cannot see. Read-only; it hands off
 * to Town Crier to change anything.
 *
 * It sits in SYSTEM, directly after Workers, and not where doc 00 filed entry 5:
 * that was the Reports section, which the A6 prune removed and which no longer
 * exists. System beside Workers because this page's capacity axis IS the worker
 * fleet — its slot count is Etl.WorkerHeartbeat, by the same liveness rule the
 * Workers page uses — so an operator reading "Overloaded at 15:00" is one row from
 * the servers that make up the pool, and the Workers row from the one place that
 * says how full the pool will be. It goes between Workers and Sources rather
 * than after Connections so as not to split that pair.
 *
 * Path `/schedules-load`, never `/schedules`: that path is Town Crier's in
 * Cartographer, and this page deep-links to it — the two sharing a path would
 * make the hand-off link indistinguishable from this page's own route.
 *
 * ---------------------------------------------------------------------------
 * OBSERVATORY COMPLETION 2026-09-29. Five rows stopped being hints and became
 * routes, each over an endpoint that already served it:
 *  - Execution History (Ship's Log, /execution-history[/:runId]) — every ETL run
 *    as one row, from Etl.EtlRun through api/observatory/executions.
 *  - Governance Queue (Muster, /governance-queue) — never /governance, which is
 *    Cartographer's route, and this page links to it.
 *  - Report Registry (Chart Room, /registry).
 *  - Retirement Candidates (Drydock, /retirement) — restored here, see above.
 *  - Queues (Holds, /queues) — api/system/dead-letters.
 *
 * The three Cartographer rows sit behind LighthouseAdmin at class level on the
 * server, so each carries `requires: 'lighthouseAdmin'` and is gated on the
 * server's own answer — the job catalogue's `permission`, read through
 * useLighthouseAdmin() — never on token roles. The Governance Queue badge polls
 * only when that answer is `admin`, so a non-admin never 403-polls. Execution
 * History and Queues carry no `requires`: their reads are bare [Authorize], and
 * the one admin-only read among them, the dead-letter peek, is gated by its page.
 *
 * The Queues badge counts messages over REACHABLE queues only. A queue the server
 * could not inspect still sends messageCount 0 as a placeholder, and it
 * contributes nothing — nothing is not zero. So no badge never claims every queue
 * is empty; the page says which ones could not be read.
 *
 * ---------------------------------------------------------------------------
 * SIGNALS AND HEALTH BUILT 2026-09-29 — the user's decisions 1 and 2. The last two
 * hints in the rail became routes, so no row is coming-soon any more:
 *  - Signal Inbox (Signal Deck, /signals) — the signal store: Etl.OperationalSignal*,
 *    raised and auto-resolved by Automaton's detector ONLY, from five rules over
 *    conditions Observatory already measures. Served by
 *    ObservatorySignalsController at api/observatory/signals, bare [Authorize] and
 *    read-only. No assign / acknowledge / resolve / snooze: whether Observatory may
 *    write at all (A1) is still open. Incidents stay out (doc 05 §6).
 *  - Health Monitoring (Beacons, /system-health) — one board, per host, over what
 *    is already measured: the API's /api/health checks, the worker fleet and source
 *    reachability. No new table; it names what nothing checks.
 *
 * The Signal Inbox badge is the only critical-toned badge, so it is also the one
 * that must never go quiet for the wrong reason. It shows OBSERVED open criticals
 * only, and only while the detector reports; when the store is unprovisioned, the
 * detector is stale or the summary cannot be read, the row shows a muted dash with
 * the reason instead of nothing — a missing badge there would read as all-clear.
 *
 * `/system-health`, not `/health…`: Vite proxies /health to the API by prefix, and
 * a nav-model test now refuses any path under a proxy key or a Static Web Apps
 * navigation-fallback exclusion.
 *
 * ---------------------------------------------------------------------------
 * REQUEST BACKSTOP ADDED 2026-10-08 (Cartographer doc 15 §7, W5-6). Not the
 * "Access Requests" queue the A6 prune dropped: Cartographer owns every request
 * and its parties decide them there. This row is rung 3 of doc 10's ladder — the
 * requests that FELL THROUGH (overdue, nobody could be told, or platform access,
 * which starts here) — and its emptiness is success.
 *
 * It is Observatory's ONE act surface over Cartographer data: D6a, amended
 * narrowly by Q-2b = a. Its interventions are ICartographerRequestBackstop's
 * (force close, reassign, reroute, a corrected due date; forward, attest and
 * refuse for platform access), LighthouseAdmin AND JwtBearer-pinned, audited by
 * the request store, and gated on the catalogue's `canHandleRequests`. Its reads
 * are LighthouseAdmin at class level, so the row `requires` it; the badge is the
 * open count on the backstop, polled only on `admin`.
 *
 * `/requests`, and `/requests/:requestId` for one request — the path a
 * Cartographer notice's `observatory:/requests/{id}` link names.
 *
 * ---------------------------------------------------------------------------
 * DATA COMB ADDED 2026-08-20. The first Signals entry that resolves to a built
 * surface rather than a hint -- served by DataCombController over the three
 * Etl.DataComb* tables the nightly sweep writes. It is the counter-example to
 * everything pruned above: the endpoint, the data and the page all exist.
 *
 * ---------------------------------------------------------------------------
 * DATA ENTRY REMOVED 2026-08-20. The DATA group is gone with it. Audit Log
 * survived the removal and moved to SYSTEM: it was never a data-entry surface,
 * and it is the only view of a trail that records every write through the API.
 */

/** Which live count feeds an item's badge. Resolved in useNavBadges(). */
export type BadgeKey = 'signalInbox' | 'governanceQueue' | 'queues' | 'requestBackstop'

export interface NavEntry {
  label: string
  /** Thematic term, shown under the label. */
  subtitle?: string
  /** Fuller descriptor for page headers. */
  description?: string
  /** Internal route. Absent for coming-soon entries. */
  path?: string
  icon: Icon
  badgeKey?: BadgeKey
  state: 'ready' | 'coming-soon'
  /** Why it isn't built yet — surfaced in the tooltip and the toast. */
  comingSoonHint?: string
  /** Opens on the Hangfire dashboard rather than routing internally. */
  externalPath?: string
  /**
   * The server-side policy this surface's endpoints sit behind. Decided from the
   * server's own answer — the job catalogue's `permission`, which IS the
   * LighthouseAdmin policy evaluated for this caller (hooks/useLighthouseAdmin.ts)
   * — never from token roles. When the server says `denied`, the row is dimmed
   * and explains itself on click, and its badge never polls. On `admin` or
   * `unknown` it routes normally and the page handles its own 403.
   */
  requires?: 'lighthouseAdmin'
}

export interface NavSection {
  /** Absent for the top-level entry (Overview). */
  label?: string
  items: NavEntry[]
  defaultOpen?: boolean
}

export const NAV_SECTIONS: NavSection[] = [
  {
    items: [
      {
        label: 'Overview', subtitle: 'The Observatory', path: '/dashboard',
        icon: IconLayoutDashboard, state: 'ready',
        description: 'Is Lighthouse healthy, what is running, and what needs attention.',
      },
    ],
  },
  {
    label: 'Operations',
    items: [
      {
        label: 'Live Activity', subtitle: 'Skywatch', path: '/etl-activity',
        icon: IconWaveSine, state: 'ready',
        description: 'Watch work move through the warehouse as it happens.',
      },
      {
        label: 'ETL Explorer', subtitle: 'Data Voyages', path: '/etl-explorer',
        icon: IconRoute, state: 'ready',
        description: 'Follow every data voyage from extraction through validation.',
      },
      {
        label: 'Scheduler', subtitle: 'Almanac', path: '/jobs',
        icon: IconCalendar, state: 'ready',
        description: 'What is scheduled, what is due, and what it depends on.',
      },
      {
        // A timeline, not a history glyph: Connections owns IconHistory, and two
        // rows sharing one icon read as one entry. No `requires` —
        // ObservatoryExecutionsController is bare [Authorize].
        label: 'Execution History', subtitle: "Ship's Log", path: '/execution-history',
        icon: IconTimeline, state: 'ready',
        description: 'Every ETL run as one row: its outcome, its window, and each entity it loaded.',
      },
    ],
  },
  {
    label: 'Signals',
    items: [
      {
        // The one Signals surface that is real. It is here rather than under
        // Operations because a comb pass is not an operation an administrator
        // runs and watches -- it is a nightly analysis whose OUTPUT is the point,
        // which is what this group is for.
        label: 'Data Comb', subtitle: 'Soundings', path: '/data-comb',
        icon: IconMicroscope, state: 'ready',
        description: 'What the nightly sweep found in the warehouse, and how long it has been true.',
      },
      {
        // No `requires`: ObservatorySignalsController is bare [Authorize] and
        // reads no ReportBuilder data. Its handling verbs (acknowledge, assign,
        // release -- D6b(i)) are LighthouseAdmin AND JwtBearer-pinned on their own
        // controller, and the page gates them on the catalogue's
        // `permission.canHandleSignals`, so the row stays open to every caller. The
        // badge is the OBSERVED open-critical count, and only while the detector
        // reports; otherwise the row shows a muted dash saying why
        // (nav/useNavBadges.ts). Acknowledging or assigning a signal never lowers
        // it: a handled signal is still open. See SIGNALS AND HEALTH BUILT above.
        label: 'Signal Inbox', subtitle: 'Signal Deck', path: '/signals',
        icon: IconInbox, badgeKey: 'signalInbox', state: 'ready',
        description: 'Raised and cleared by the detector from what Observatory already measures; acknowledged and assigned by people, never resolved by them.',
      },
      {
        // `/system-health`, never `/health…`: Vite proxies /health to the API by
        // PREFIX (vite.config.ts), so a /health-monitoring route would be served
        // by the API in development. No `requires` and no badgeKey: every read is
        // bare [Authorize] or anonymous, none touches ReportBuilder data, and a
        // count of unhealthy checks is a status figure, not a queue.
        label: 'Health Monitoring', subtitle: 'Beacons', path: '/system-health',
        icon: IconHeartbeat, state: 'ready',
        description: 'What each host reports about the things it depends on, and what nothing checks.',
      },
    ],
  },
  {
    label: 'Cartographer',
    items: [
      {
        // `/governance-queue`, never `/governance`: that path is Cartographer's,
        // and this page links to it. The badge is the ownership-gap count alone
        // (nav/useNavBadges.ts). ObservatoryGovernanceController is LighthouseAdmin
        // at class level.
        label: 'Governance Queue', subtitle: 'Muster', path: '/governance-queue',
        icon: IconClipboardCheck, badgeKey: 'governanceQueue', state: 'ready',
        requires: 'lighthouseAdmin',
        description: 'Reports with no accountable owner, grants about to lapse, and schedules running under nobody.',
      },
      {
        // ObservatoryRegistryController is LighthouseAdmin at class level, and so is
        // ObservatoryReportDetailController, which serves Report Detail at
        // /registry/:reportId (D5) -- under this path so findNavEntry's prefix match
        // keeps this row highlighted on one report.
        label: 'Report Registry', subtitle: 'Chart Room', path: '/registry',
        icon: IconBook, state: 'ready', requires: 'lighthouseAdmin',
        description: 'Every report as a managed operational asset.',
      },
      {
        // Restored from the A6 prune — see the Retirement clause above. After the
        // Registry because every candidate is a registry report. No badgeKey: a
        // candidate is a suggestion, not a queue of things needing attention.
        // ObservatoryRetirementController and the episodes controller are
        // LighthouseAdmin at class level.
        label: 'Retirement Candidates', subtitle: 'Drydock', path: '/retirement',
        icon: IconArchive, state: 'ready', requires: 'lighthouseAdmin',
        description: 'Reports that appear unused, and what has happened to each one flagged.',
      },
      {
        // See REQUEST BACKSTOP ADDED above. Last in the section because it is the
        // one row here that acts: the three above read and hand off. The badge is
        // the open count on the backstop (nav/useNavBadges.ts) — each one is a
        // request nobody else is going to move. ObservatoryRequestsBackstopController
        // is LighthouseAdmin at class level; its writes are pinned on each action.
        label: 'Request Backstop', subtitle: 'Breakwater', path: '/requests',
        icon: IconLifebuoy, badgeKey: 'requestBackstop', state: 'ready', requires: 'lighthouseAdmin',
        description: 'Cartographer requests that fell through: overdue, told to nobody, or waiting on a Lighthouse administrator.',
      },
    ],
  },
  {
    label: 'System',
    defaultOpen: false,
    items: [
      {
        // Kept when Data Entry was retired, and re-homed rather than deleted: this is the only
        // human-readable view of the audit trail, the interceptor behind it records every warehouse and
        // reporting write through the API, and Cartographer's governance auditor writes to the same
        // table. "Chronicle" stays -- that word means the audit trail everywhere else too.
        // AuditLogController is LighthouseAdmin at class level.
        label: 'Audit Log', subtitle: 'Chronicle', path: '/audit',
        icon: IconListDetails, state: 'ready', requires: 'lighthouseAdmin',
        description: 'Every change made through the API, who made it, and what it replaced.',
      },
      {
        // No badgeKey, deliberately: a live worker count is a capacity figure, not
        // a queue of things needing attention, and the rail's badge means the
        // latter everywhere else.
        label: 'Workers', subtitle: 'Engines', path: '/workers',
        icon: IconCpu, state: 'ready',
        description: 'Every background-job server, its slots, and when it last reported.',
      },
      {
        // Directly after Workers because its capacity axis IS the worker fleet —
        // see SCHEDULES RESTORED above. No badgeKey: an Overloaded quarter-hour is
        // a forecast, not a queue of things needing attention, and a badge counting
        // Indeterminate buckets would count "this page cannot say" as a problem.
        // A calendar-with-a-chart glyph: Scheduler (Almanac) already owns the plain
        // calendar, and two adjacent-in-meaning rows with one icon read as one entry.
        // ObservatoryScheduleLoadController is LighthouseAdmin at class level.
        label: 'Schedules', subtitle: 'Tide Tables', path: '/schedules-load',
        icon: IconCalendarStats, state: 'ready', requires: 'lighthouseAdmin',
        description: 'What is scheduled to run, when, and whether the shared worker pool can take it.',
      },
      {
        // No badgeKey, deliberately, and for the same reason Workers above has none:
        // source reachability is a status figure, not a queue of things needing
        // attention, and the rail's badge means the latter everywhere else. A badge
        // here would also have to count unobserved rows as problems, which is the one
        // reading this whole surface exists to refuse.
        label: 'Sources', subtitle: 'Stations', path: '/sources',
        icon: IconPlugConnected, state: 'ready',
        description: 'Whether each upstream source is answering, which host asked, and what it last said.',
      },
      {
        // Sits immediately below Sources because the two are one question in two
        // tenses, and an operator who finds a source unreachable reads its history
        // here. No badgeKey: a count of connections is not a count of things needing
        // attention, and on the ordinary day the honest count is zero.
        label: 'Connections', subtitle: 'Moorings', path: '/connections',
        // A history glyph, not a second plug. Sources already owns the plug, and two
        // adjacent rows with one icon read as a duplicated entry -- which is the exact
        // confusion these two pages have already cost once.
        icon: IconHistory, state: 'ready',
        description: 'Every connection the ETL opened to a source over 30 days, and what each one carried.',
      },
      {
        // Entry 10, the last of the walkthrough. Read-only in this version (doc 16
        // §9), and the icon is a plain gear for that reason: sliders or a toggle
        // would promise adjustment the page deliberately does not offer. No
        // badgeKey -- a count of settings that are off is not a queue of things
        // needing attention, since nobody has ruled whether they should be off.
        // ObservatoryConfigurationController is LighthouseAdmin at class level.
        label: 'Configuration', subtitle: 'Helm', path: '/configuration',
        icon: IconSettings, state: 'ready', requires: 'lighthouseAdmin',
        description: 'Every allowlisted setting, per host, as of its process start — and what is silently off.',
      },
      {
        // No `requires`: the depths are bare [Authorize] and only the message peek
        // is LighthouseAdmin, which the page gates itself. The badge counts
        // reachable queues only (nav/useNavBadges.ts).
        label: 'Queues', subtitle: 'Holds', path: '/queues',
        icon: IconStack2, badgeKey: 'queues', state: 'ready',
        description: 'Dead-letter queues: how deep each one is, and what is sitting in it.',
      },
      {
        label: 'Hangfire Dashboard', icon: IconGauge, state: 'ready',
        externalPath: '/hangfire',
        description: 'The raw job dashboard.',
      },
    ],
  },
]

/** Flattened, for breadcrumb and route lookups. */
export const NAV_ENTRIES: { section?: string; entry: NavEntry }[] = NAV_SECTIONS.flatMap(
  (section) => section.items.map((entry) => ({ section: section.label, entry })),
)

/**
 * Finds the nav entry owning a pathname, preferring the longest matching path so
 * `/admin/data-entry/audit` resolves to Audit Log rather than Data Entry Admin.
 */
export function findNavEntry(pathname: string) {
  return NAV_ENTRIES
    .filter(({ entry }) => entry.path && (pathname === entry.path || pathname.startsWith(entry.path + '/')))
    .sort((a, b) => (b.entry.path?.length ?? 0) - (a.entry.path?.length ?? 0))[0]
}
